import api from 'services/api';
import { REJECT, array, nullable, num, object, refuseDegraded, str } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// A PROJECTION CONTRACT over `GET /scheduling_windows` — see ../../README.md. Self-contained on
// purpose: its own type, its own schema, shared with nothing. The full list read is
// `getSchedulingWindows`, which validates every window's rules and links; a picker reads three
// fields of each row, and declaring only those is what lets it refuse a degraded answer without
// refusing on drift it does not care about. `object()` ignores the rest of the payload.
//
// Backend: routes/scheduling_windows.js, GET /scheduling_windows.
// Supported query parameters (all optional but `limit`, whose backend default is 10):
//   name                  ILIKE '%name%' on the name alone
//   search                ILIKE on name OR description OR zone name — not used here, so that every
//                         row a picker shows contains what the user typed
//   zone_name             exact equals (an array becomes `= ANY`)
//   scheduling_window_id  exact equals (an array becomes `= ANY`) — resolves a stored id to its label
//   sortColumn            one of an allow-list; 'name' is the one we use
//   page / offset         `offset` takes precedence

/** A scheduling window as a picker deals in it. */
export interface SchedulingWindowSummary {
  readonly scheduling_window_id: number;
  readonly name: string;
  // Nullable because `scheduling_windows.zone_id` is nullable and the list SQL LEFT JOINs zones.
  readonly zone_name: string | null;
}

const summarySchema = object<SchedulingWindowSummary>({
  scheduling_window_id: num(REJECT),
  name: str(),
  zone_name: nullable(str()),
});

interface SchedulingWindowsSearchResponse {
  readonly nodes: readonly SchedulingWindowSummary[];
  readonly pagination: { readonly total_records: number };
}

const responseSchema = object<SchedulingWindowsSearchResponse>({
  // The route has always called its rows `nodes`; unrelated to node definitions.
  nodes: array(summarySchema),
  // Parsed to a number server-side, so a string here is drift.
  pagination: object<{ readonly total_records: number }>({ total_records: num(REJECT) }),
});

export interface SearchSchedulingWindowsParams {
  readonly limit: number;
  readonly name?: string;
  readonly zone_name?: string;
  readonly scheduling_window_id?: number;
}

/** One page of options, with the server-side count of all matches. */
export interface SchedulingWindowsSearchPage {
  readonly items: readonly SchedulingWindowSummary[];
  readonly total: number;
}

// In-flight de-duplication, keyed on the exact query: independent callers that ask the same
// question at the same time share one request rather than one each. Nothing is cached past
// settlement — asking again later is a genuinely new question, since the underlying records
// change under us.
const inFlight = new Map<string, Promise<SchedulingWindowsSearchPage>>();

// Key on the query's entries, sorted, so two callers that build the same query with their keys
// in a different order still share one request.
const queryKey = (query: SearchSchedulingWindowsParams): string =>
  JSON.stringify(
    Object.entries(query)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : 1))
  );

/**
 * One page of scheduling windows matching `params`, ordered by name.
 *
 * Name order is fixed here rather than left to callers, so that no caller can present an
 * unordered list — the route's own default is `created_at`. The column is `COLLATE "C"`, so it
 * is byte order: every uppercase initial sorts before every lowercase one.
 *
 * Collapses with `refuseDegraded`, and that is a property of this contract rather than a policy
 * imposed on an unknown consumer: an option missing from the list is one the user cannot pick,
 * and one they picked before no longer resolves. A partial list of values is not a list of
 * values.
 *
 * `undefined` params are dropped by axios, so omitting one widens the query rather than
 * filtering on empty.
 */
export const searchSchedulingWindows = async (params: SearchSchedulingWindowsParams): Promise<SchedulingWindowsSearchPage> => {
  const key = queryKey(params);
  const pending = inFlight.get(key);
  if (pending !== undefined) return pending;

  const request = api
    .get('/scheduling_windows', { params: { ...params, sortColumn: 'name', sortOrder: 'ASC' } })
    .then((res) => refuseDegraded(readResponse(res.data, { schema: responseSchema, source: 'searchSchedulingWindows' })))
    .then(({ nodes, pagination }) => ({ items: nodes, total: pagination.total_records }))
    .finally(() => inFlight.delete(key));
  inFlight.set(key, request);
  return request;
};
